/**
 * IndexedDB Service for Offline-First Storage
 * Stores all data locally in browser IndexedDB for offline access
 * Syncs with cloud when user is online and has enabled cloud sync
 */

const DB_NAME = 'HisabOKHaiDB';
const DB_VERSION = 1;

class IndexedDBService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    if (this.isInitialized) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log('IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores (tables)

        // Users store
        if (!db.objectStoreNames.contains('users')) {
          const userStore = db.createObjectStore('users', { keyPath: 'id', autoIncrement: true });
          userStore.createIndex('email', 'email', { unique: true });
          userStore.createIndex('cloudSyncEnabled', 'cloudSyncEnabled', { unique: false });
        }

        // Ledgers store
        if (!db.objectStoreNames.contains('ledgers')) {
          const ledgerStore = db.createObjectStore('ledgers', { keyPath: 'id', autoIncrement: true });
          ledgerStore.createIndex('userId', 'userId', { unique: false });
          ledgerStore.createIndex('cloudId', 'cloudId', { unique: false });
          ledgerStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        }

        // Transactions store
        if (!db.objectStoreNames.contains('transactions')) {
          const txStore = db.createObjectStore('transactions', { keyPath: 'id', autoIncrement: true });
          txStore.createIndex('userId', 'userId', { unique: false });
          txStore.createIndex('ledgerId', 'ledgerId', { unique: false });
          txStore.createIndex('categoryId', 'categoryId', { unique: false });
          txStore.createIndex('transactionDate', 'transactionDate', { unique: false });
          txStore.createIndex('transactionType', 'transactionType', { unique: false });
          txStore.createIndex('cloudId', 'cloudId', { unique: false });
          txStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        }

        // Categories store
        if (!db.objectStoreNames.contains('categories')) {
          const catStore = db.createObjectStore('categories', { keyPath: 'id', autoIncrement: true });
          catStore.createIndex('userId', 'userId', { unique: false });
          catStore.createIndex('type', 'type', { unique: false });
          catStore.createIndex('parentCategoryId', 'parentCategoryId', { unique: false });
          catStore.createIndex('cloudId', 'cloudId', { unique: false });
        }

        // Reminders store
        if (!db.objectStoreNames.contains('reminders')) {
          const reminderStore = db.createObjectStore('reminders', { keyPath: 'id', autoIncrement: true });
          reminderStore.createIndex('userId', 'userId', { unique: false });
          reminderStore.createIndex('reminderDate', 'reminderDate', { unique: false });
          reminderStore.createIndex('cloudId', 'cloudId', { unique: false });
        }

        // Recurring Expenses store
        if (!db.objectStoreNames.contains('recurringExpenses')) {
          const recStore = db.createObjectStore('recurringExpenses', { keyPath: 'id', autoIncrement: true });
          recStore.createIndex('userId', 'userId', { unique: false });
          recStore.createIndex('isActive', 'isActive', { unique: false });
          recStore.createIndex('cloudId', 'cloudId', { unique: false });
        }

        // Generated Expenses store
        if (!db.objectStoreNames.contains('generatedExpenses')) {
          const genStore = db.createObjectStore('generatedExpenses', { keyPath: 'id', autoIncrement: true });
          genStore.createIndex('userId', 'userId', { unique: false });
          genStore.createIndex('recurringExpenseId', 'recurringExpenseId', { unique: false });
          genStore.createIndex('status', 'status', { unique: false });
          genStore.createIndex('expectedDate', 'expectedDate', { unique: false });
        }

        // Budgets store
        if (!db.objectStoreNames.contains('budgets')) {
          const budgetStore = db.createObjectStore('budgets', { keyPath: 'id', autoIncrement: true });
          budgetStore.createIndex('userId', 'userId', { unique: false });
          budgetStore.createIndex('categoryId', 'categoryId', { unique: false });
          budgetStore.createIndex('ledgerId', 'ledgerId', { unique: false });
          budgetStore.createIndex('cloudId', 'cloudId', { unique: false });
        }

        // Sync Queue store (for offline changes)
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('entityType', 'entityType', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
          syncStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // App Settings store
        if (!db.objectStoreNames.contains('appSettings')) {
          db.createObjectStore('appSettings', { keyPath: 'userId' });
        }
      };
    });
  }

  /**
   * Generic CRUD operations
   */

  async add(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      // Add sync metadata
      const dataWithMeta = {
        ...data,
        syncStatus: 'pending', // pending, synced, failed
        localCreatedAt: new Date().toISOString(),
        localUpdatedAt: new Date().toISOString(),
      };

      const request = store.add(dataWithMeta);

      request.onsuccess = () => {
        const id = request.result;
        resolve({ ...dataWithMeta, id });
      };

      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, id) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName, indexName = null, indexValue = null) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);

      let request;
      if (indexName && indexValue !== null) {
        const index = store.index(indexName);
        request = index.getAll(indexValue);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async update(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const dataWithMeta = {
        ...data,
        syncStatus: 'pending',
        localUpdatedAt: new Date().toISOString(),
      };

      const request = store.put(dataWithMeta);

      request.onsuccess = () => resolve(dataWithMeta);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, id) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(entityType, entityId, operation, data) {
    const queueItem = {
      entityType,
      entityId,
      operation, // create, update, delete
      data,
      status: 'pending', // pending, syncing, completed, failed
      retryCount: 0,
      createdAt: new Date().toISOString(),
    };

    return this.add('syncQueue', queueItem);
  }

  /**
   * Get pending sync items
   */
  async getPendingSyncItems() {
    return this.getAll('syncQueue', 'status', 'pending');
  }

  /**
   * Mark sync item as completed
   */
  async markSyncCompleted(id) {
    const item = await this.get('syncQueue', id);
    if (item) {
      item.status = 'completed';
      item.syncedAt = new Date().toISOString();
      return this.update('syncQueue', item);
    }
  }

  /**
   * Check if user has cloud sync enabled
   */
  async isCloudSyncEnabled() {
    const userId = this.getCurrentUserId();
    if (!userId) return false;

    const settings = await this.get('appSettings', userId);
    return settings?.cloudSyncEnabled || false;
  }

  /**
   * Get current user ID from localStorage
   */
  getCurrentUserId() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || null;
  }

  /**
   * Export all data (for backup/export feature)
   */
  async exportAllData() {
    await this.init();
    const data = {};

    const storeNames = Array.from(this.db.objectStoreNames);

    for (const storeName of storeNames) {
      data[storeName] = await this.getAll(storeName);
    }

    return data;
  }

  /**
   * Import data (for restore feature)
   */
  async importData(data) {
    await this.init();

    for (const [storeName, items] of Object.entries(data)) {
      if (this.db.objectStoreNames.contains(storeName)) {
        await this.clear(storeName);
        for (const item of items) {
          await this.add(storeName, item);
        }
      }
    }

    return true;
  }
}

// Create singleton instance
const indexedDBService = new IndexedDBService();

export default indexedDBService;
